import { Check, MessageSquarePlus, Pencil, Trash2 } from 'lucide-react'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useReminderActions, type ReminderItem } from '../hooks/use-reminders'
import { useStartReminderChat } from './reminders/hooks/use-start-reminder-chat'
import { CellActionsCluster, undoCellAction, type CellAction } from './cell-actions'
import { HOVER_CLUSTER_TRIGGER } from './cell-hover-buttons'

interface ReminderActionsOptions {
  item: ReminderItem
  /**
   * Focus the title for in-place edit. Absent on hosts that open the Reminder
   * View instead — those omit the move rather than pointing it at a missing
   * caret.
   */
  onEdit?: () => void
  /**
   * Completed but still held in its list: the moves collapse to Undo until the
   * user leaves. Hosts that keep resolved items as a record (meeting For You,
   * a chat turn's artifacts) never pass this.
   */
  lingering?: boolean
  /** After a resolve/unresolve the host wants to flash as saved (Home's indicator). */
  onSaving?: () => void
  /** Which entry point fired the move, for Amplitude. */
  trigger: string
}

/**
 * What a reminder offers in its current state, for both surfaces that offer it
 * — the hover cluster and the right-click menu — so delete / mark-done /
 * linger-undo can't drift between them or between hosts. Edit is a slot: only
 * a cell with a live title has a caret to focus.
 */
export function useReminderCellActions({
  item,
  onEdit,
  lingering = false,
  onSaving,
  trigger,
}: ReminderActionsOptions): Array<CellAction> {
  const { deleteItem, setResolved } = useReminderActions()
  const startReminderChat = useStartReminderChat()
  const resolved = item.resolved === true

  const remove: CellAction = {
    key: 'delete',
    label: 'Delete reminder',
    icon: <Trash2 />,
    destructive: true,
    onSelect: () => deleteItem(item.id, { trigger }),
  }

  if (lingering) {
    return [
      undoCellAction(() => {
        setResolved(item.id, false, { trigger })
        onSaving?.()
      }),
    ]
  }

  // Nothing left to mark done or edit on a resolved reminder — but it can still
  // be thrown away, which is what the menu is for (the cluster hides itself).
  if (resolved) return [remove]

  return [
    ...(onEdit
      ? [
          {
            key: 'edit',
            label: 'Edit reminder',
            icon: <Pencil />,
            onSelect: () => {
              // Only the click is tracked here — a committed change still fires
              // the regular Edit event from `updateItem` on blur.
              AmplitudeRenderer.trackEvent('Reminder Action', { type: 'Start Edit', reminderId: item.id, trigger })
              onEdit()
            },
          } satisfies CellAction,
        ]
      : []),
    {
      key: 'chat',
      label: 'Start a chat',
      icon: <MessageSquarePlus />,
      onSelect: () => void startReminderChat(item, trigger),
    },
    remove,
    {
      key: 'done',
      label: 'Mark as done',
      icon: <Check />,
      tone: 'brand',
      onSelect: () => {
        setResolved(item.id, true, { trigger })
        onSaving?.()
      },
    },
  ]
}

interface ReminderRowHoverButtonsProps extends Omit<ReminderActionsOptions, 'trigger'> {
  className?: string
}

/**
 * The reminder row's floating quick moves (Figma 8425:91176) — same cluster
 * Home and meeting For You share.
 *
 * Selecting by state, not hiding: the linger's take-back is hover-gated like
 * the resting moves (HIG-5928).
 */
export function ReminderRowHoverButtons({ className, ...options }: ReminderRowHoverButtonsProps) {
  // Chat takes Edit's slot in the cluster: the title is already a click-to-edit field, and the menu keeps Edit.
  const actions = useReminderCellActions({ ...options, trigger: HOVER_CLUSTER_TRIGGER }).filter(
    (action) => action.key !== 'edit',
  )
  const resolved = options.item.resolved === true

  if (resolved && !options.lingering) return null

  return <CellActionsCluster actions={actions} className={className} />
}
